import React, { useState, useEffect } from 'react'
    import { motion } from 'framer-motion'
    import * as FiIcons from 'react-icons/fi'
    import SafeIcon from '../common/SafeIcon'
    import RelationshipSelect from '../common/RelationshipSelect'
    import {
      getRelationshipBasedOnSex,
      getBaseRelationshipType,
    } from '../utils/relationshipMappings'

    const { FiX, FiSave, FiUser, FiHeart } = FiIcons

    // Organized relationship options with proper grouping (flattened for HTML compatibility)
    const relationshipGroups = [
      {
        name: "FAMILY",
        options: [
          // 1st Generation
          { type: 'parent', label: 'Parent (Father/Mother)', group: '1st Generation' },
          { type: 'child', label: 'Child (Son/Daughter)', group: '1st Generation' },
          { type: 'sibling', label: 'Sibling (Brother/Sister)', group: '1st Generation' },
          { type: 'spouse', label: 'Spouse (Husband/Wife)', group: '1st Generation' },
          { type: 'ex_spouse', label: 'Ex-Spouse (Ex-Husband/Ex-Wife)', group: '1st Generation' },
          { type: 'partner', label: 'Partner', group: '1st Generation' },
          { type: 'step_parent', label: 'Step-parent (Stepfather/Stepmother)', group: '1st Generation' },
          { type: 'step_child', label: 'Step-child (Stepson/Stepdaughter)', group: '1st Generation' },
          { type: 'step_sibling', label: 'Step-sibling (Stepbrother/Stepsister)', group: '1st Generation' },
          { type: 'half_sibling', label: 'Half-sibling (Half-brother/Half-sister)', group: '1st Generation' },
          { type: 'adoptive_parent', label: 'Adoptive Parent', group: '1st Generation' },
          { type: 'adoptive_child', label: 'Adopted Child', group: '1st Generation' },
          // 2nd Generation
          { type: 'grandparent', label: 'Grandparent (Grandfather/Grandmother)', group: '2nd Generation' },
          { type: 'grandchild', label: 'Grandchild (Grandson/Granddaughter)', group: '2nd Generation' },
          { type: 'parental_sibling', label: 'Parental Sibling (Aunt/Uncle)', group: '2nd Generation' },
          { type: 'sibling_child', label: 'Sibling\'s Child (Niece/Nephew)', group: '2nd Generation' },
          { type: 'great_grandparent', label: 'Great-Grandparent', group: '2nd Generation' },
          { type: 'great_grandchild', label: 'Great-Grandchild', group: '2nd Generation' },
          // 3rd Generation+
          { type: 'great_great_grandparent', label: 'Great-Great-Grandparent', group: '3rd Generation+' },
          { type: 'grandparent_sibling', label: 'Grandparent\'s Sibling (Great-aunt/Great-uncle)', group: '3rd Generation+' },
          { type: 'grandsibling_child', label: 'Grand-sibling\'s Child (Grandniece/Grandnephew)', group: '3rd Generation+' },
          { type: 'cousin', label: 'Cousin (1st Cousin)', group: '3rd Generation+' },
          { type: 'second_cousin', label: 'Second Cousin', group: '3rd Generation+' },
          { type: 'third_cousin', label: 'Third Cousin', group: '3rd Generation+' },
          { type: 'first_cousin_once_removed', label: 'First Cousin Once Removed', group: '3rd Generation+' },
          { type: 'first_cousin_twice_removed', label: 'First Cousin Twice Removed', group: '3rd Generation+' },
          { type: 'second_cousin_once_removed', label: 'Second Cousin Once Removed', group: '3rd Generation+' },
          // In-Laws
          { type: 'sibling_in_law', label: 'Sibling-in-law (Brother-in-law/Sister-in-law)', group: 'In-Laws' },
          { type: 'parent_in_law', label: 'Parent-in-law (Father-in-law/Mother-in-law)', group: 'In-Laws' },
          { type: 'child_in_law', label: 'Child-in-law (Son-in-law/Daughter-in-law)', group: 'In-Laws' },
          { type: 'cousin_in_law', label: 'Cousin-in-law', group: 'In-Laws' },
          { type: 'sibling_child_in_law', label: 'Niece-in-law / Nephew-in-law', group: 'In-Laws' },
          { type: 'parental_sibling_in_law', label: 'Aunt-in-law / Uncle-in-law', group: 'In-Laws' },
        ]
      },
      {
        name: "OTHER RELATIONSHIPS",
        options: [
          // Professional
          { type: 'colleague', label: 'Colleague', group: 'Professional' },
          { type: 'boss', label: 'Boss', group: 'Professional' },
          { type: 'employee', label: 'Employee', group: 'Professional' },
          // Personal
          { type: 'friend', label: 'Friend', group: 'Personal' }
        ]
      }
    ];

    const EditRelationshipForm = ({
      relationship,
      person,
      people,
      onSubmit,
      onCancel,
    }) => {
      const [relationshipType, setRelationshipType] = useState('')
      const [loading, setLoading] = useState(false)

      useEffect(() => {
        if (relationship?.currentRelationshipType) {
          const baseType = getBaseRelationshipType(relationship.currentRelationshipType)
          setRelationshipType(baseType)
        }
      }, [relationship])

      const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
          await onSubmit({
            relationshipType: relationshipType,
          })
        } catch (error) {
          console.error('Error updating relationship:', error)
        } finally {
          setLoading(false)
        }
      }

      const otherPerson = people.find(p => p.id === relationship.otherPersonId)

      return (
        <motion.div
          key="edit-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Edit Relationship</h2>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Cancel"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="bg-blue-100 p-2 rounded-full">
                  <SafeIcon icon={FiUser} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{person.name}</p>
                  <p className="text-sm text-gray-500">This person</p>
                </div>
              </div>
              <div className="text-center text-gray-400">
                <SafeIcon icon={FiHeart} />
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="bg-green-100 p-2 rounded-full">
                  <SafeIcon icon={FiUser} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{relationship.otherPersonName}</p>
                  <p className="text-sm text-gray-500">Is the...</p>
                </div>
              </div>
              <div>
                <label
                  htmlFor="relationshipType"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Relationship to {person.name}
                </label>
                <RelationshipSelect
                  id="relationshipType"
                  value={relationshipType}
                  onChange={setRelationshipType}
                  relationshipGroups={relationshipGroups}
                  required
                />
                {otherPerson && (
                  <p className="text-xs text-gray-500 mt-1">
                    This will be updated to:{' '}
                    <strong>
                      {getRelationshipBasedOnSex(relationshipType, otherPerson.sex)}
                    </strong>
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <SafeIcon icon={FiSave} className="mr-2" />
                )}
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </motion.div>
      )
    }

    export default EditRelationshipForm